import {
  getOperatorsLabel,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  tableFilterOperatorLookupTable,
  TableFilter,
  TableFilterOperators,
  TableFilterOperatorsUnionLookup,
  ValuePresenceOperator,
} from './types';
import { Operator } from '@ardoq/api-types';

export const getTableFilterOperators = <FilterType extends TableFilter['type']>(
  filterType: FilterType
): TableFilterOperators[FilterType] => {
  return tableFilterOperatorLookupTable[filterType];
};

/**
 * Every operator resolved by the `string` label function: the comparison
 * operators of the string and number types, and the value presence pair.
 */
type ComparisonOperator =
  | TableFilterOperatorsUnionLookup['string']
  | TableFilterOperatorsUnionLookup['number']
  | ValuePresenceOperator;

export type TableFilterOperatorLabels = {
  // Used by string and number operators (delegated to @ardoq/query-builder by
  // default).
  string: (operator: ComparisonOperator) => string;
  // Dates read as is / after / before rather than equal to / greater / less.
  date: (
    operator: TableFilterOperatorsUnionLookup['date'] | ValuePresenceOperator
  ) => string;
  list: {
    is: string;
    isAnyOf: string;
    isNot: string;
  };
  boolean: {
    is: string;
    isNot: string;
  };
};

export const getDefaultTableFilterOperatorLabels = (
  i18nLocale: I18nLocale
): TableFilterOperatorLabels => ({
  string: operator => getOperatorsLabel(operator, i18nLocale),
  date: operator =>
    getOperatorsLabel(operator, i18nLocale, QueryBuilderFilterType.DATE),
  list: {
    is: t(i18nLocale, 'tableFilterBar:operators.list.is', 'is'),
    isAnyOf: t(
      i18nLocale,
      'tableFilterBar:operators.list.isAnyOf',
      'is any of'
    ),
    isNot: t(i18nLocale, 'tableFilterBar:operators.list.isNot', 'is not'),
  },
  boolean: {
    is: t(i18nLocale, 'tableFilterBar:operators.boolean.is', 'is'),
    isNot: t(i18nLocale, 'tableFilterBar:operators.boolean.isNot', 'is not'),
  },
});

/** @deprecated Use {@link getDefaultTableFilterOperatorLabels} with an `I18nLocale`. */
export const defaultTableFilterOperatorLabels: TableFilterOperatorLabels =
  getDefaultTableFilterOperatorLabels(DEFAULT_ARDOQ_I18NLOCALE);

export const getTableFilterStringOperatorLabel = (
  operator: TableFilterOperatorsUnionLookup['string'] | ValuePresenceOperator,
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => labels.string(operator);

export const getTableFilterDateOperatorLabel = (
  operator: TableFilterOperatorsUnionLookup['date'] | ValuePresenceOperator,
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => labels.date(operator);

export const getTableFilterNumberOperatorLabel = (
  operator: TableFilterOperatorsUnionLookup['number'] | ValuePresenceOperator,
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => labels.string(operator);

/**
 * Resolved by the same label function as the string operators, so `is empty`
 * reads identically here and in the query builder without a second set of
 * translation keys.
 */
export const getTableFilterValuePresenceOperatorLabel = (
  operator: ValuePresenceOperator,
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => labels.string(operator);

export const getTableFilterListOperatorLabel = (
  operator: TableFilterOperatorsUnionLookup['list'] | ValuePresenceOperator,
  hasMultipleItemsInSelection: boolean,
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => {
  switch (operator) {
    case Operator.CONTAINS:
      if (hasMultipleItemsInSelection) {
        return labels.list.isAnyOf;
      }
      return labels.list.is;
    case Operator.NOT_CONTAINS:
      return labels.list.isNot;
    case Operator.IS_EMPTY:
    case Operator.IS_NOT_EMPTY:
      return getTableFilterValuePresenceOperatorLabel(operator, labels);
  }
};

export const getTableFilterBooleanOperatorLabel = (
  operator: TableFilterOperatorsUnionLookup['boolean'],
  labels: TableFilterOperatorLabels = defaultTableFilterOperatorLabels
): string => {
  switch (operator) {
    case Operator.EQUAL:
      return labels.boolean.is;
    case Operator.NOT_EQUAL:
      return labels.boolean.isNot;
  }
};
